package com.crowpay.views.components.project.work.changeOrders

import com.crowpay.ChangeRequest
import com.crowpay.views.theming.AppColors
import com.crowpay.ChangeRequestItem
import com.crowpay.ItemChange
import com.crowpay.ItemChangeInterface
import com.crowpay.LineItemFromChangeRequest
import com.crowpay.LineItemInterface
import com.crowpay.PendingItemChange
import com.crowpay.PendingLineItem
import com.crowpay.extensions.isUnread
import com.crowpay.sdk.UnreadHandler
import com.crowpay.sdk.notNullSession
import com.crowpay.views.components.project.work.changeOrders.ChangeRequestData.Status
import com.crowpay.views.components.project.work.payApps.DirectlyDisplayableState
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.invokeAllSafe

data class ChangeRequestItemWrapper(
    val wrapped: ChangeRequestItem,
    val name: String,
    val originalPrice: Long,
) : ChangeRequestItem by wrapped {
    val id get() = wrapped.itemNumber
    val totalPrice: Long get() = originalPrice + (priceChange ?: 0L)
    override val priceChange: Long? get() = if (wrapped.cancelled) -originalPrice else wrapped.priceChange
}

enum class ChangeStatus(override val displayName: String, override val color: Color) : DirectlyDisplayableState {
    Accepted("Approved", AppColors.secondary.main),
    Rejected("Rejected", AppColors.red.main),
    PendingApproval("Pending Approval", AppColors.grey.main);

    override val priority: Int get() = 0
}
val ChangeRequestItem.status: ChangeStatus get() = when {
    changeRejected != null -> ChangeStatus.Rejected
    changeAccepted != null -> ChangeStatus.Accepted
    else -> ChangeStatus.PendingApproval
}

fun ChangeRequest.status(items: List<ChangeRequestItem>) =
    items.map { it.status }.toSet().let { itemStates ->
        return@let when {
            published == null || itemStates.isEmpty() -> Status.Draft
            itemStates.size == 1 -> when (itemStates.single()) {
                ChangeStatus.Accepted -> Status.Accepted
                ChangeStatus.Rejected -> Status.Rejected
                ChangeStatus.PendingApproval -> Status.PendingApproval
            }
            itemStates.any { it == ChangeStatus.PendingApproval } -> Status.InReview
            itemStates.none { it == ChangeStatus.PendingApproval } -> Status.PartiallyAccepted
            else -> Status.Draft
        }
    }

data class ChangeRequestData(
    val changeRequest: ChangeRequest,
    val changes: List<ChangeRequestItemWrapper>,
) {
    enum class Status(override val displayName: String, override val color: Color) : DirectlyDisplayableState {
        Draft("Draft", AppColors.grey.main),
        PendingApproval("Submitted", AppColors.primary.main),
        InReview("Open - Partial", AppColors.yellow.main),
        PartiallyAccepted("Closed - Partial", AppColors.yellow.main),
        Accepted("Approved", AppColors.secondary.main),
        Rejected("Rejected", AppColors.red.main),
        ;

        override val priority: Int get() = 0
    }

    val status = changeRequest.status(changes)

    val sorted = changes.sortTypes()
    val newItems get() = sorted.pendingItems
    val itemChanges = sorted.pendingChanges.filterNot { it.cancelled }
    val itemCancellations = sorted.pendingChanges.filter { it.cancelled }
}

val ChangeRequestItem.cancelled: Boolean get() {
    return when (this) {
        is ChangeRequestItemWrapper -> wrapped.cancelled
        is LineItemInterface -> false
        is ItemChangeInterface -> cancelled
        else -> throw IllegalArgumentException("Unsupported ChangeRequestItem type")
    }
}



data class SortedChangeItems(
    val pendingChanges: List<PendingItemChange>,
    val pendingItems: List<PendingLineItem>,
    val itemChanges: List<ItemChange>,
    val lineItems: List<LineItemFromChangeRequest>
)

fun Collection<ChangeRequestItem>.sortTypes(): SortedChangeItems {
    val pendingChanges = ArrayList<PendingItemChange>()
    val pendingItems = ArrayList<PendingLineItem>()
    val changes = ArrayList<ItemChange>()
    val lines = ArrayList<LineItemFromChangeRequest>()

    fun sort(item: ChangeRequestItem) {
        when (item) {
            is ChangeRequestItemWrapper -> sort(item.wrapped)
            is PendingItemChange -> pendingChanges.add(item)
            is PendingLineItem -> pendingItems.add(item)
            is ItemChange -> changes.add(item)
            is LineItemFromChangeRequest -> lines.add(item)
        }
    }

    for (element in this) sort(element)

    return SortedChangeItems(pendingChanges, pendingItems, changes, lines)
}

object ChangeItemReadReceipts {
    suspend fun markAllAsRead(items: Collection<ChangeRequestItem>) {
        val sorted = items.sortTypes()

        val session = notNullSession()
        session.pendingItemChange.markAsRead(sorted.pendingChanges)
        session.pendingLineItems.markAsRead(sorted.pendingItems)
        session.itemChanges.markAsRead(sorted.itemChanges)

        val unread = sorted.lineItems.filter { it.isUnread(session.userId) }.map { it._id }
        if (unread.isNotEmpty()) {
            val updates = session.lineItems.endpoints.markAsRead(unread).associateBy { it._id }
            session.lineItems.localMapUpdate(updates)
        }
    }

    private fun List<()->Unit>.reduce() = when (size) {
        0 -> null
        1 -> first()
        else -> toList()::invokeAllSafe
    }

    suspend fun markAllAsReadDelayUpdate(items: Collection<ChangeRequestItem>): (() -> Unit)? {
        val sorted = items.sortTypes()
        val updates = ArrayList<()->Unit>(4)

        val session = notNullSession()
        session.pendingItemChange.markAsReadDelayUpdate(sorted.pendingChanges)?.let(updates::add)
        session.pendingLineItems.markAsReadDelayUpdate(sorted.pendingItems)?.let(updates::add)
        session.itemChanges.markAsReadDelayUpdate(sorted.itemChanges)?.let(updates::add)

        val unread = sorted.lineItems.filter { it.isUnread(session.userId) }.map { it._id }
        if (unread.isNotEmpty()) {
            val updateMap = session.lineItems.endpoints.markAsRead(unread).associateBy { it._id }
            updates.add { session.lineItems.localMapUpdate(updateMap) }
        }

        return updates.reduce()
    }

    suspend fun markFeedbackAsReadDelayUpdate(items: Collection<ChangeRequestItem>): (() -> Unit)? {
        val sorted = items.sortTypes()

        val session = notNullSession()
        return listOfNotNull(
            session.pendingItemChange.markFeedbackAsReadDelayUpdate(sorted.pendingChanges),
            session.pendingLineItems.markFeedbackAsReadDelayUpdate(sorted.pendingItems)
        ).reduce()
    }

    private fun List<UnreadHandler>.toHandler() = if (isEmpty()) UnreadHandler.Never else reduce { acc, handler -> acc + handler }

    suspend fun handle(items: Collection<ChangeRequestItem>): UnreadHandler {
        val sorted = items.sortTypes()
        val session = notNullSession()

        return listOfNotNull(
            if (sorted.pendingChanges.isNotEmpty()) session.pendingItemChange.handle { sorted.pendingChanges } else null,
            if (sorted.pendingItems.isNotEmpty()) session.pendingLineItems.handle { sorted.pendingItems } else null,
            if (sorted.itemChanges.isNotEmpty()) session.itemChanges.handle { sorted.itemChanges } else null,
            if (sorted.lineItems.isNotEmpty()) UnreadHandler(
                "LineItemFromChangeRequest",
                unread = { sorted.lineItems.isUnread(session.userId) },
                markRead = {
                    val updates = session.lineItems.endpoints.markAsRead(sorted.lineItems.map { it._id }).associateBy { it._id }
                    session.lineItems.localMapUpdate(updates)
                }
            ) else null
        ).toHandler()
    }

    suspend fun handleDelayUpdate(items: Collection<ChangeRequestItem>): UnreadHandler {
        val sorted = items.sortTypes()
        val session = notNullSession()

        return listOfNotNull(
            if (sorted.pendingChanges.isNotEmpty()) session.pendingItemChange.handleDelayUpdate { sorted.pendingChanges } else null,
            if (sorted.pendingItems.isNotEmpty()) session.pendingLineItems.handleDelayUpdate { sorted.pendingItems } else null,
            if (sorted.itemChanges.isNotEmpty()) session.itemChanges.handleDelayUpdate { sorted.itemChanges } else null,
            if (sorted.lineItems.isNotEmpty()) UnreadHandler(
                "LineItemFromChangeRequest",
                unread = { sorted.lineItems.isUnread(session.userId) },
                markReadDelayUpdate = {
                    val updates = session.lineItems.endpoints.markAsRead(sorted.lineItems.map { it._id }).associateBy { it._id };
                    { session.lineItems.localMapUpdate(updates) }
                }
            ) else null
        ).toHandler()
    }



    suspend fun handleFeedback(items: Collection<ChangeRequestItem>): UnreadHandler {
        val sorted = items.sortTypes()
        val session = notNullSession()

        return listOfNotNull(
            if (sorted.pendingChanges.isNotEmpty()) session.pendingItemChange.handleFeedback { sorted.pendingChanges } else null,
            if (sorted.pendingItems.isNotEmpty()) session.pendingLineItems.handleFeedback { sorted.pendingItems } else null,
        ).toHandler()
    }

    suspend fun markAsReadDelayUpdate(item: ChangeRequestItem): (() -> Unit)? = when (item) {
        is ChangeRequestItemWrapper -> markAsReadDelayUpdate(item.wrapped)
        is PendingItemChange -> notNullSession().pendingItemChange.markAsReadDelayUpdate(listOf(item))
        is PendingLineItem -> notNullSession().pendingLineItems.markAsReadDelayUpdate(listOf(item))
        is ItemChange -> notNullSession().itemChanges.markAsReadDelayUpdate(listOf(item))
        is LineItemFromChangeRequest -> {
            val session = notNullSession()
            val update = session.lineItems.endpoints.markAsRead(listOf(item._id)).firstOrNull();
            {
                session.lineItems.localSignalUpdate(
                    matching = { it._id == update?._id },
                    modify = { update }
                )
            }
        }
        else -> null
    }

    suspend fun markAsRead(item: ChangeRequestItem) = markAsReadDelayUpdate(item)?.invoke()
}
